import { useState } from 'react'
import {
  CheckCircle2,
  Mail,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Check,
  Settings,
} from 'lucide-react'

type InstitutionalDisclosureStatus = 'not_started' | 'submitted' | 'acknowledged' | 'not_required'

interface ComplianceOfficer {
  name: string
  title?: string
  email: string
  phone?: string
}

interface Props {
  school: { name: string } | null
  institutionalStatus: InstitutionalDisclosureStatus
  institutionalDeadline: string
  institutionalSubmittedAt?: string
  complianceOfficer?: ComplianceOfficer
  campaign?: any
  user?: any
  onMarkSubmitted: () => void
}

function getDaysRemaining(isoDeadline: string): number {
  const today = new Date()
  const deadline = new Date(isoDeadline)
  const diff = deadline.getTime() - today.getTime()
  return Math.ceil(diff / (1000 * 60 * 60 * 24))
}

function getCountdownColor(days: number): string {
  if (days > 3) return '#C0FD72'
  if (days >= 1) return '#F9DA4C'
  return '#FFA07A'
}

function getCountdownText(days: number): string {
  if (days > 3) return `${days} days remaining`
  if (days === 1) return '1 day — act soon'
  if (days > 0) return `${days} days — act soon`
  return 'Overdue — submit immediately'
}

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

function buildDealSummary(campaign: any, user: any): string {
  if (!campaign) return ''

  const athleteName = user?.name ?? 'Unknown Athlete'
  const school = user?.school?.name ?? ''
  const nilGoFields = campaign.nilGoFields ?? {}
  const brandName = nilGoFields.brandLegalName ?? campaign.brand?.name ?? ''
  const payout = nilGoFields.totalCompensation ?? campaign.payout ?? ''
  const startDate = nilGoFields.agreementStartDate ?? campaign.dealExecutedAt ?? ''
  const endDate = nilGoFields.agreementEndDate ?? campaign.endDate ?? ''
  const nature = nilGoFields.natureOfRelationship ?? 'Social Media Endorsement'
  const description = nilGoFields.serviceDescription ?? campaign.instructions ?? ''
  const paymentStructure = nilGoFields.paymentStructure ?? ''
  const brandContactName = nilGoFields.brandContactName ?? ''
  const brandContactEmail = nilGoFields.brandContactEmail ?? ''

  const lines: string[] = [
    `NIL DEAL DISCLOSURE`,
    ``,
    `Athlete: ${athleteName}`,
    school ? `School: ${school}` : '',
    ``,
    `--- Deal Details ---`,
    brandName ? `Brand/Company: ${brandName}` : '',
    nature ? `Nature of Relationship: ${nature}` : '',
    payout
      ? `Total Compensation: $${parseFloat(payout).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : '',
    startDate ? `Agreement Start Date: ${formatDate(startDate)}` : '',
    endDate ? `Agreement End Date: ${formatDate(endDate)}` : '',
    description ? `Services/Deliverables: ${description}` : '',
    paymentStructure ? `Payment Structure: ${paymentStructure}` : '',
    ``,
    `--- Brand Contact ---`,
    brandContactName ? `Name: ${brandContactName}` : '',
    brandContactEmail ? `Email: ${brandContactEmail}` : '',
  ]

  return lines.filter((l) => l !== '').join('\n')
}

const InstitutionalDisclosurePanel = ({
  school,
  institutionalStatus,
  institutionalDeadline,
  institutionalSubmittedAt,
  complianceOfficer,
  campaign,
  user,
  onMarkSubmitted,
}: Props) => {
  const [disclosureOpen, setDisclosureOpen] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState(false)
  const [copiedAll, setCopiedAll] = useState(false)
  const [attested, setAttested] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const schoolName = school?.name ?? null

  if (!schoolName) {
    return (
      <div className="bg-white/10 border border-white/10 rounded-2xl p-5">
        <p className="text-white font-semibold text-sm mb-3">Institutional Disclosure</p>
        <p className="text-white/40 text-sm">
          Add your school in Settings to track institutional disclosure deadlines.
        </p>
      </div>
    )
  }

  const daysRemaining = getDaysRemaining(institutionalDeadline)
  const countdownColor = getCountdownColor(daysRemaining)

  const handleCopyEmail = async () => {
    if (!complianceOfficer) return
    await navigator.clipboard.writeText(complianceOfficer.email)
    setCopiedEmail(true)
    setTimeout(() => setCopiedEmail(false), 2000)
  }

  const handleCopyAll = async () => {
    const text = buildDealSummary(campaign, user)
    await navigator.clipboard.writeText(text)
    setCopiedAll(true)
    setTimeout(() => setCopiedAll(false), 2500)
  }

  const handleMarkSubmitted = async () => {
    setIsSubmitting(true)
    await onMarkSubmitted()
    setIsSubmitting(false)
  }

  const isDone = institutionalStatus === 'submitted' || institutionalStatus === 'acknowledged'

  const mailtoHref = complianceOfficer
    ? `mailto:${complianceOfficer.email}?subject=${encodeURIComponent(`NIL Deal Disclosure — ${user?.name ?? 'Athlete'}`)}`
    : undefined

  return (
    <div className="bg-white/10 border border-white/10 rounded-2xl p-5 space-y-5">
      {/* Header */}
      <div>
        <p className="text-white font-semibold text-sm">Institutional Disclosure</p>
        <p className="text-white/40 text-xs mt-0.5">{schoolName}</p>
      </div>

      {/* Deadline countdown */}
      {institutionalStatus !== 'not_required' && !isDone && (
        <div className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
          <Clock className="w-4 h-4 shrink-0" style={{ color: countdownColor }} />
          <div>
            <p
              className={`text-sm font-semibold ${daysRemaining <= 0 ? 'animate-pulse' : ''}`}
              style={{ color: countdownColor }}
            >
              {getCountdownText(daysRemaining)}
            </p>
            <p className="text-white/40 text-xs">
              Disclosure due by {formatDate(institutionalDeadline)}
            </p>
          </div>
        </div>
      )}

      {/* Already done states */}
      {institutionalStatus === 'submitted' && (
        <div className="flex items-center gap-2 bg-[#C0FD72]/8 border border-[#C0FD72]/20 rounded-xl px-4 py-3">
          <CheckCircle2 className="w-4 h-4 text-[#C0FD72] shrink-0" />
          <div>
            <p className="text-[#C0FD72] text-sm font-semibold">Disclosed to {schoolName} ✓</p>
            {institutionalSubmittedAt && (
              <p className="text-white/40 text-xs mt-0.5">
                Submitted {formatDate(institutionalSubmittedAt)}
              </p>
            )}
          </div>
        </div>
      )}

      {institutionalStatus === 'acknowledged' && (
        <div className="flex items-center gap-2 bg-[#C0FD72]/8 border border-[#C0FD72]/20 rounded-xl px-4 py-3">
          <CheckCircle2 className="w-4 h-4 text-[#C0FD72] shrink-0" />
          <p className="text-[#C0FD72] text-sm font-semibold">Acknowledged by {schoolName} ✓</p>
        </div>
      )}

      {institutionalStatus === 'not_required' && (
        <p className="text-white/40 text-sm">
          No institutional disclosure required for this deal.
        </p>
      )}

      {/* Main action flow — only shown when not yet done */}
      {!isDone && institutionalStatus !== 'not_required' && (
        <>
          {/* Instruction */}
          <p className="text-white/50 text-sm leading-relaxed">
            Email your compliance officer with your deal details. Per Bylaw 22, the disclosure must
            include all parties' contact info, nature of the relationship, services, term length,
            total compensation, and payment structure.
          </p>

          {/* Compliance officer card */}
          {complianceOfficer ? (
            <div className="bg-white/5 border border-white/15 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/15 border border-white/20 flex items-center justify-center shrink-0">
                  <span className="text-white/80 text-sm font-semibold">
                    {getInitials(complianceOfficer.name)}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold leading-tight">
                    {complianceOfficer.name}
                  </p>
                  {complianceOfficer.title && (
                    <p className="text-white/50 text-xs mt-0.5">{complianceOfficer.title}</p>
                  )}
                </div>
              </div>

              {/* Email address — prominent */}
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2">
                <Mail className="w-3.5 h-3.5 text-white/40 shrink-0" />
                <span className="text-white/80 text-sm font-mono flex-1 truncate select-all">
                  {complianceOfficer.email}
                </span>
                <button
                  onClick={handleCopyEmail}
                  className="shrink-0 p-1 text-white/40 hover:text-white/70 transition-colors"
                  title="Copy email address"
                >
                  {copiedEmail ? (
                    <Check className="w-3.5 h-3.5 text-[#C0FD72]" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              {complianceOfficer.phone && (
                <p className="text-white/30 text-xs px-1">{complianceOfficer.phone}</p>
              )}

              <a
                href={mailtoHref}
                className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 hover:border-white/40 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
              >
                <Mail className="w-4 h-4" />
                Open Email to {complianceOfficer.name}
              </a>
            </div>
          ) : (
            <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
              <p className="text-white/40 text-sm">
                No compliance officer on file for {schoolName}.
              </p>
              <a
                href="/settings"
                className="shrink-0 flex items-center gap-1.5 text-white/50 hover:text-white/80 border border-white/20 hover:border-white/40 rounded-lg px-3 py-1.5 text-xs transition-colors"
              >
                <Settings className="w-3.5 h-3.5" />
                Settings
              </a>
            </div>
          )}

          {/* Copy deal details */}
          <div className="space-y-2">
            <button
              onClick={handleCopyAll}
              className="w-full flex items-center justify-center gap-2 bg-white/8 border border-white/15 hover:border-white/30 text-white text-sm font-semibold rounded-xl py-3 transition-all"
            >
              {copiedAll ? (
                <>
                  <Check className="w-4 h-4 text-[#C0FD72]" />
                  <span className="text-[#C0FD72]">Deal Details Copied</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Deal Details to Clipboard
                </>
              )}
            </button>

            <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
              <button
                onClick={() => setDisclosureOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 text-white/50 hover:text-white/70 transition-colors text-xs"
              >
                <span>What's included in the copied text</span>
                {disclosureOpen ? (
                  <ChevronDown className="w-3.5 h-3.5 shrink-0" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5 shrink-0" />
                )}
              </button>

              {disclosureOpen && (
                <div className="px-4 pb-4 border-t border-white/10">
                  <p className="text-white/40 text-xs leading-relaxed pt-3">
                    Athlete name, school, brand/company name, nature of relationship, total
                    compensation, agreement dates, services/deliverables, payment structure, and
                    brand contact info.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Attestation */}
          <div className="space-y-3 pt-1">
            <button
              onClick={() => setAttested((v) => !v)}
              className="w-full flex items-center gap-3 bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/20 rounded-xl px-4 py-3.5 transition-colors text-left"
            >
              <div
                className={`w-4 h-4 rounded shrink-0 border flex items-center justify-center transition-colors ${attested ? 'bg-[#C0FD72] border-[#C0FD72]' : 'border-white/30'}`}
              >
                {attested && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
              </div>
              <span
                className={`text-sm transition-colors ${attested ? 'text-white' : 'text-white/50'}`}
              >
                I have emailed my compliance officer
              </span>
            </button>

            <button
              onClick={handleMarkSubmitted}
              disabled={!attested || isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-[#C0FD72]/10 hover:bg-[#C0FD72]/20 border border-[#C0FD72]/30 hover:border-[#C0FD72]/60 text-[#C0FD72] text-sm font-semibold rounded-xl py-3.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
            <p className="text-white/25 text-xs text-center">
              Logs this disclosure as submitted in your compliance record.
            </p>
          </div>
        </>
      )}
    </div>
  )
}

export default InstitutionalDisclosurePanel
